import { AIModelType, classifyChecklistWriteError, IOrganization } from "@/api";
import { OrganizationsAutocomplete } from "@/components/admin/form-elements/OrganizationsAutocomplete";
import { Button } from "@/components/shared/buttons/Button";
import type { EntityComboboxValidity } from "@/components/shared/EntityCombobox";
import {
    Box,
    Checkbox,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material";
import { FormTextFieldElement } from "@rhf-kit/mui";
import { useEffect, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { ChecklistGroupField } from "./ChecklistGroupField";
import { AIModelSelect } from "./form-elements/AIModelSelect";

export interface IClauseChecklistFormValues {
    name: string;
    description: string;
    clauses: number[];
    organization: IOrganization | null;
    organization_group_id: number | null;
    modelType: AIModelType;
    show_gaps: boolean;
}

interface IProps {
    /**
     * Callback to close the dialog
     */
    onClose: () => void;
    /**
     * An error that occurred during the mutation
     */
    error: Error | null;
    /**
     * The group the server refused on the last save
     */
    refusedGroupId: number | null;
    /**
     * A reason the last save failed that belongs to the whole form
     */
    failure: string | null;
}

/**
 * The form used to create or edit a clause checklist
 */
export const ClauseChecklistForm = ({
    onClose,
    error,
    refusedGroupId,
    failure,
}: IProps) => {
    const {
        setError,
        clearErrors,
        control,
        formState: { isSubmitted },
    } = useFormContext<IClauseChecklistFormValues>();
    const groupId = useWatch({ control, name: "organization_group_id" });
    const groupValidity = useRef<EntityComboboxValidity>("empty");

    useEffect(() => {
        const failure = error ? classifyChecklistWriteError(error) : null;
        if (
            failure?.kind === "validation" &&
            failure.status === 400 &&
            failure.detail !== null
        ) {
            setError("name", {
                type: "manual",
                message: failure.detail,
            });
        } else {
            clearErrors("name");
        }
    }, [clearErrors, error, setError]);

    return (
        <Stack width={"100%"} gap={3} alignItems={"center"}>
            <FormTextFieldElement
                label="Title"
                name="name"
                size={"small"}
                fullWidth
                required
            />
            <FormTextFieldElement
                label="Description"
                name="description"
                size={"small"}
                fullWidth
                required
                variant="standard"
            />

            <OrganizationsAutocomplete
                multiple={false}
                required={groupId === null}
            />

            <Controller
                name="organization_group_id"
                control={control}
                rules={{
                    validate: (value: number | null) =>
                        value === null || groupValidity.current === "valid",
                }}
                render={({ field: { onChange, value } }) => (
                    <div className="w-full">
                        <ChecklistGroupField
                            value={value}
                            onChange={onChange}
                            onValidityChange={(validity) => {
                                groupValidity.current = validity;
                            }}
                            refused={
                                refusedGroupId !== null &&
                                refusedGroupId === value
                            }
                            showValidation={isSubmitted}
                        />
                    </div>
                )}
            />

            <AIModelSelect />

            <Controller
                name="show_gaps"
                control={control}
                render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={value ?? true}
                                onChange={(e) => onChange(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">
                                    Check for Coverage Gaps
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    When disabled, gap information is hidden
                                    from topic summaries, headlines, and the
                                    scope overview
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: "flex-start", width: "100%" }}
                    />
                )}
            />

            {failure && (
                <Typography
                    role="alert"
                    variant="body2"
                    color="error"
                    width={"100%"}
                >
                    {failure}
                </Typography>
            )}

            <Stack direction={"row"} gap={3} px={1} width={"100%"}>
                <Button
                    type="button"
                    variant="outlined"
                    color="dark"
                    sx={{ borderRadius: 2, py: 1 }}
                    fullWidth
                    onClick={onClose}
                    disableElevation
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="dark"
                    sx={{ borderRadius: 2, py: 1 }}
                    fullWidth
                    disableElevation
                >
                    Save
                </Button>
            </Stack>
        </Stack>
    );
};
